import React, { useState } from 'react';
import { X } from 'lucide-react';

interface CreateProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (projectData: {
    title: string;
    description: string;
    targetInterviews: number;
  }) => Promise<void>;
  isLoading: boolean;
}

export function CreateProjectModal({
  isOpen,
  onClose,
  onSubmit,
  isLoading
}: CreateProjectModalProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [targetInterviews, setTargetInterviews] = useState('');
  const [targetInterviewsError, setTargetInterviewsError] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTargetInterviewsError('');

    // Validate target interviews if provided
    if (targetInterviews) {
      const numValue = Number(targetInterviews);
      if (isNaN(numValue) || !Number.isInteger(numValue) || numValue < 1) {
        setTargetInterviewsError('Please enter a valid whole number greater than 0');
        return;
      }
    }

    await onSubmit({
      title: title.trim(),
      description: description.trim(),
      targetInterviews: targetInterviews ? Number(targetInterviews) : 0
    });
    
    setTitle('');
    setDescription('');
    setTargetInterviews('');
  };

  return (
    <div className="fixed inset-0 z-10 overflow-y-auto">
      <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
          <div className="absolute top-0 right-0 pt-4 pr-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-orange focus:ring-offset-2"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="sm:flex sm:items-start">
            <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
              <h3 className="text-lg font-medium leading-6 text-brand-teal">
                Create New Project
              </h3>
              <form onSubmit={handleSubmit} className="mt-4 space-y-4">
                <div>
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                    Project Title
                  </label>
                  <input
                    type="text"
                    id="title"
                    required
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:ring-2 focus:ring-brand-orange focus:border-brand-orange transition-colors"
                    placeholder="e.g., Customer Feedback for Product X"
                  />
                </div>

                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                    Description
                  </label>
                  <textarea
                    id="description"
                    rows={3}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:ring-2 focus:ring-brand-orange focus:border-brand-orange transition-colors"
                    placeholder="Brief description of the project goals..."
                  />
                </div>

                <div>
                  <label htmlFor="targetInterviews" className="block text-sm font-medium text-gray-700">
                    Target Number of Interviews
                  </label>
                  <input
                    type="text"
                    id="targetInterviews"
                    value={targetInterviews}
                    onChange={(e) => {
                      setTargetInterviews(e.target.value);
                      setTargetInterviewsError('');
                    }}
                    className={`mt-1 block w-full rounded-lg shadow-sm focus:ring-2 focus:ring-brand-orange transition-colors ${
                      targetInterviewsError ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Optional - enter a number"
                  />
                  {targetInterviewsError && (
                    <p className="mt-1 text-sm text-red-600">{targetInterviewsError}</p>
                  )}
                </div>

                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={isLoading || !title.trim()}
                    className="inline-flex w-full justify-center rounded-lg border border-transparent bg-brand-orange px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-brand-orange-light focus:outline-none focus:ring-2 focus:ring-brand-orange focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isLoading ? 'Creating...' : 'Create Project'}
                  </button>
                  <button
                    type="button"
                    onClick={onClose}
                    className="mt-3 inline-flex w-full justify-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand-orange focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}